import { FormField as FormFieldType } from '../document/pages/Assistance/useDocumentConfig';

/**
 * Maps a masterdata lookup option (ArticleLookupType, ClientLookupType, …) to
 * the form fields it should populate when the customer picks it.
 *
 * Mirrors the server-side mapping that Assistance uses once the backend has
 * a record to mutate:
 *   - `documents/extraction/extractors/utils/article_number_utils.update_article_numbers_from_result`
 *   - `documents/extraction/extractors/utils/customer_utils.update_customer_field_from_result`
 *   - `documents/extraction/extractors/utils/address_utils.update_address_field_from_result`
 *
 * The public form has no backend extraction pipeline, so we replicate the
 * mapping client-side. Keys are the *target* name (a FormField's `valueKey`
 * for subfields, or its `fieldName` for flat fields); values are the
 * camelCase option property as returned by the GraphQL lookup.
 */

type TargetFieldToOptionProp = Record<string, string>;

// `description` is the literal GraphQL field on ArticleLookupType (not
// `articleDescription` — that's OfferMatchingLookupType).
const ARTICLE_MAPPING: TargetFieldToOptionProp = {
    article_id1: 'articleId1',
    article_id2: 'articleId2',
    article_id3: 'articleId3',
    // A bare `article_number` field (line_items__article_number without subfield
    // suffix) also maps to the primary article id.
    article_number: 'articleId1',
    article_number_2: 'articleId2',
    article_number_3: 'articleId3',
    article_description: 'description',
    article_description_master_data: 'description',
    article_partition_specific_number: 'articlePartitionSpecificNumber',
    gross_price: 'grossPrice',
    article_partition: 'partitionId',
};

// `CLIENT_MASTERDATA_ENTITY` fields → customer / address-style targets.
const CLIENT_MAPPING: TargetFieldToOptionProp = {
    customer_number: 'clientId1',
    customer_number2: 'clientId2',
    name: 'companyName',
    company_name: 'companyName',
    vat_id: 'vat',
    iln: 'iln',
    customer_type: 'clientType',
    internal_note: 'internalNote',
    contact_person: 'clientContactPerson1',
    misc: 'misc',
    // customer__address sub-subfields also pull from the client record
    street_and_nr: 'address1',
    street_name: 'streetName',
    street_number: 'streetNumber',
    postcode: 'zipCode',
    city: 'city',
    country_subdivision: 'countrySubdivision',
    country: 'country',
};

const ADDRESS_MAPPING: TargetFieldToOptionProp = {
    address_id: 'addressId1',
    address_id2: 'addressId2',
    address_id3: 'addressId3',
    // In Assistance the `contact_person` field of an address is used as `name`.
    name: 'contactPerson',
    company_name: 'name',
    street_and_nr: 'address1',
    street_name: 'streetName',
    street_number: 'streetNumber',
    postcode: 'zipCode',
    city: 'city',
    country_subdivision: 'countrySubdivision',
    country: 'country',
    email: 'email',
    phone: 'phone',
    misc: 'misc',
};

const CONTACT_MAPPING: TargetFieldToOptionProp = {
    contact_id: 'contactId',
    name: 'fullName',
    full_name: 'fullName',
    email: 'email',
    phone: 'phone',
};

const FRAMEWORK_CONTRACT_MAPPING: TargetFieldToOptionProp = {
    framework_contract_number: 'contractId1',
    contract_id1: 'contractId1',
};

const MAPPINGS: Record<string, TargetFieldToOptionProp> = {
    ArticleLookupType: ARTICLE_MAPPING,
    ClientLookupType: CLIENT_MAPPING,
    AddressLookupType: ADDRESS_MAPPING,
    ContactLookupType: CONTACT_MAPPING,
    FrameworkContractLookupType: FRAMEWORK_CONTRACT_MAPPING,
};

const DEFAULT_VALUE_KEY = 'value';

/**
 * For a FormField, return the name we use to look up the option property.
 * Subfields use valueKey (e.g. `street_name`); flat fields fall back to
 * fieldName (e.g. `article_description`).
 */
const targetNameFor = (f: FormFieldType): string => {
    if (f.valueKey && f.valueKey !== DEFAULT_VALUE_KEY) return f.valueKey;
    return f.fieldName || '';
};

/**
 * Compute the set of field-name → value updates a given lookup option would
 * apply to a scope. Only returns updates for fields that are currently empty
 * so any manual entry by the customer is preserved.
 */
export const computeLookupBackfills = (
    option: any,
    scopeFields: FormFieldType[],
    scopeValues: Record<string, unknown>
): { values: Record<string, unknown>; unitChoices: string[] | null; unitField: FormFieldType | null } => {
    const result: Record<string, unknown> = {};
    let unitChoices: string[] | null = null;
    let unitField: FormFieldType | null = null;

    if (!option || !option.__typename) {
        return { values: result, unitChoices, unitField };
    }

    const mapping = MAPPINGS[option.__typename];
    if (!mapping) return { values: result, unitChoices, unitField };

    for (const f of scopeFields) {
        const target = targetNameFor(f);
        const optionProp = mapping[target];
        if (!optionProp) continue;
        const incoming = option?.[optionProp];
        const key = (f.config as any).name;
        result[key] = incoming ?? '';
    }

    // Article-specific: `orderUnits` is a list of valid units for the article.
    // When present, it becomes the unit field's choices (dynamic override) and
    // the selected value switches to the first valid option if the current
    // value isn't already in the list. Mirrors `update_article_unit_choices`
    // / `update_article_unit_field` in the backend.
    if (option.__typename === 'ArticleLookupType' && Array.isArray(option.orderUnits) && option.orderUnits.length > 0) {
        unitField = scopeFields.find((f) => targetNameFor(f) === 'unit') || null;
        if (unitField) {
            unitChoices = (option.orderUnits as unknown[]).map(String);
            const key = (unitField.config as any).name;
            const current = scopeValues[key];
            const currentStr = current == null ? '' : String(current);
            if (!currentStr || !unitChoices.includes(currentStr)) {
                result[key] = unitChoices[0];
            }
        }
    }

    return { values: result, unitChoices, unitField };
};
